import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Target, Settings, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { useCollegeContext } from '../../../contexts/CollegeContext';
import { HubStateProvider } from '../../../contexts/HubStateContext';
import { toast } from 'react-hot-toast';

// Import components
import TabNavigation from './components/TabNavigation';
import CollegeSelector from './components/CollegeSelector';
import ProgressDashboard from './components/ProgressDashboard';
import AdaptiveHeader from './components/AdaptiveHeader';

// Import views
import StrategicOverview from './views/StrategicOverview';
import TierAnalysis from './views/TierAnalysis';
import PortfolioStrategy from './views/PortfolioStrategy';

const CollegeComparisonHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useFamilyContext();
  const { academicProfile, personalProfile, assessmentData, removeCollege } = useCollegeContext();

  // Determine initial tab from URL path
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('/tier-analysis')) return 'tier';
    if (path.includes('/portfolio-strategy')) return 'portfolio';
    return 'strategic';
  };

  // Core state
  const [colleges, setColleges] = useState([]);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [portfolioStatus, setPortfolioStatus] = useState({});
  const [loading, setLoading] = useState(true);

  // Access control state
  const [hasAccess, setHasAccess] = useState(false);
  const [accessError, setAccessError] = useState(null);

  // View management - simplified for tab-based navigation
  const [activeTab, setActiveTab] = useState(getInitialTab()); // 'strategic', 'tier', 'portfolio'
  const [includeAdditional, setIncludeAdditional] = useState(false);

  // Create combined student profile for AI analysis
  const studentProfile = {
    ...academicProfile,
    ...personalProfile,
    assessment_data: assessmentData
  };

  // Debug logging for student profile
  useEffect(() => {
    console.log('🎓 [CollegeComparisonHub] Student Profile created:', {
      academicProfileKeys: academicProfile ? Object.keys(academicProfile) : 'null/undefined',
      personalProfileKeys: personalProfile ? Object.keys(personalProfile) : 'null/undefined',
      combinedProfileKeys: studentProfile ? Object.keys(studentProfile) : 'null/undefined',
      profileSample: {
        sat_total: studentProfile?.sat_total,
        gpa_weighted: studentProfile?.gpa_weighted,
        act_composite: studentProfile?.act_composite,
        intended_major: studentProfile?.intended_major
      },
      profileIsEmpty: !studentProfile || Object.keys(studentProfile).length === 0
    });
  }, [academicProfile, personalProfile, assessmentData]);

  // Check access on mount
  useEffect(() => {
    if (currentUser?.id) {
      checkComparisonHubAccess();
    }
  }, [currentUser]);

  // Fetch user's colleges and portfolio status after access is granted
  useEffect(() => {
    if (hasAccess && currentUser?.id) {
      fetchUserColleges();
      fetchPortfolioStatus();
    }
  }, [hasAccess, currentUser]);

  const checkComparisonHubAccess = async () => {
    if (!currentUser?.id) {
      setHasAccess(false);
      return;
    }

    // Parents always have access
    if (currentUser.isParent || currentUser.type === 'parent') {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    try {
      const { data: accessSetting, error } = await supabase
        .from('college_access_settings')
        .select('has_comparison_hub_access')
        .eq('child_id', currentUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const hasDbAccess = accessSetting?.has_comparison_hub_access === true;
      setHasAccess(hasDbAccess);
      setAccessError(null);

      if (!hasDbAccess) {
        console.log('[ComparisonHub] Access denied for user:', currentUser.id);
      }

    } catch (err) {
      console.error('[ComparisonHub] Error checking access:', err);
      setAccessError(err.message);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserColleges = async () => {
    try {

        setLoading(true);

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser.id;

      console.log('🔍 [HUB] Fetching colleges for user:', userIdForQuery);

      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select(`
          *,
          global_colleges (
            id,
            name,
            location,
            city,
            state,
            acceptance_rate,
            cs_ranking,
            usnwr_ranking,
            business_ranking,
            engineering_ranking,
            liberal_arts_ranking,
            tuition_in_state,
            tuition_out_of_state,
            room_board,
            total_cost,
            sat_range,
            act_range,
            test_optional,
            student_enrollment,
            campus_setting,
            popular_majors,
            special_programs,
            meets_full_need,
            average_aid,
            average_aid_package,
            merit_aid_available,
            avg_merit_aid,
            percent_receiving_aid,
            no_loan_policy,
            student_faculty_ratio,
            study_abroad_programs,
            honors_programs,
            housing_options,
            campus_culture,
            student_qualities,
            core_values,
            notable_alumni,
            campus_culture_details,
            popular_major_classes,
            major_culture_details,
            interdisciplinary_options,
            post_grad_outcomes,
            popular_interest_programs,
            social_impact_programs,
            ed_deadline,
            ea_deadline,
            ed2_deadline,
            rd_deadline,
            finaid_deadline,
            css_deadline,
            merit_deadline,
            honors_deadline
          )
        `)
        .eq('user_id', userIdForQuery)
        .is('removed_at', null)  // Only get active colleges
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      console.log('📥 [HUB] Fetched', data?.length || 0, 'colleges');

      // Log what advantage fields we got from the database
      if (data && data.length > 0) {
        data.forEach(college => {
          const name = college.global_colleges?.name || college.name;
          console.log(`  [${name}] Fields from DB:`, {
            has_student_advantages: !!college.student_advantages,
            has_school_fit: !!college.school_fit,
            has_competitive_position: !!college.competitive_position,
            has_academic_fit_analysis: !!college.academic_fit_analysis,
            has_culture_fit_analysis: !!college.culture_fit_analysis,
            has_career_fit_score: college.career_fit_score !== null && college.career_fit_score !== undefined,
            academic_fit_is_empty: college.academic_fit_analysis ? Object.keys(college.academic_fit_analysis).length === 0 : null,
            culture_fit_is_empty: college.culture_fit_analysis ? Object.keys(college.culture_fit_analysis).length === 0 : null,
            has_deadlines: !!(college.global_colleges?.ed_deadline || college.global_colleges?.ea_deadline || college.global_colleges?.rd_deadline)
          });
        });
      }

      setColleges(data || []);
      console.log('✅ [HUB] Colleges state updated');

      // Auto-select first 3-4 colleges if none selected
      if (data && data.length >= 3 && selectedColleges.length === 0) {
        const autoSelect = data
          .filter(c => c.category !== 'additional' && c.category !== 'other')
          .slice(0, 4)
          .map(c => c.id);
        setSelectedColleges(autoSelect);
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };


  const fetchPortfolioStatus = async () => {
    try {
      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser.id;

      const { data, error } = await supabase.rpc(
        'get_portfolio_completion_status',
        { p_user_id: userIdForQuery }
      );

      if (error) throw error;
      setPortfolioStatus(data || {});

      // Auto-select appropriate tab based on unlock status
      if (data?.view3_unlocked) {
        setActiveTab('portfolio');
      } else if (data?.view2_unlocked) {
        setActiveTab('tier');
      } else if (data?.view1_unlocked) {
        setActiveTab('strategic');
      }
    } catch (error) {
      console.error('Error fetching portfolio status:', error);
    }
  };

  // Handle college deletion
  const handleDeleteCollege = async (collegeId) => {
    try {
      const college = colleges.find(c => c.id === collegeId);
      const collegeName = college?.global_colleges?.name || college?.name || 'College';

      await removeCollege(collegeId);
      toast.success(`Removed ${collegeName} from your list`);

      // Refresh colleges
      await fetchUserColleges();
    } catch (error) {
      console.error('Error deleting college:', error);
      toast.error('Failed to remove college');
    }
  };

  const handleTabChange = (tabName) => {
    // Check if tab is unlocked
    const isUnlocked = {
      strategic: portfolioStatus.view1_unlocked,
      tier: portfolioStatus.view2_unlocked,
      portfolio: portfolioStatus.view3_unlocked
    }[tabName];

    if (!isUnlocked) {
      // Show requirement message with link
      const requirementInfo = {
        strategic: {
          message: 'Add at least 3 colleges to unlock Strategic Overview',
          action: 'Go to My Schools'
        },
        tier: {
          message: 'Complete at least one tier (4 colleges) to unlock Tier Analysis',
          action: 'Go to My Schools'
        },
        portfolio: {
          message: 'Complete and lock all 3 tiers to unlock Portfolio Strategy',
          action: 'Go to My Schools'
        }
      }[tabName];

      toast.error(
        (t) => (
          <div className="flex flex-col gap-2">
            <div className="font-medium">{requirementInfo.message}</div>
            <button
              onClick={() => {
                navigate('/college/data-hub');
                toast.dismiss(t.id);
              }}
              className="text-left text-azure-600 hover:text-azure-700 font-semibold underline text-sm"
            >
              {requirementInfo.action} →
            </button>
          </div>
        ),
        {
          icon: '🔒',
          duration: 6000,
          style: {
            maxWidth: '400px'
          }
        }
      );
      return;
    }

    setActiveTab(tabName);
  };

  // Get selected colleges data
  const comparisonData = colleges.filter(c => selectedColleges.includes(c.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading comparison data...</p>
        </div>
      </div>
    );
  }

  // Show access denied message if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border-2 border-slate-900 p-12 text-center max-w-md">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Coming Soon!
          </h2>
          <p className="text-gray-600 mb-6">
            The College Comparison Hub is currently being developed.
            Check back soon for powerful tools to compare your college options side-by-side.
          </p>
          <button
            onClick={() => navigate('/college')}
            className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Back to College Journey
          </button>
          {accessError && (
            <p className="text-red-600 text-xs mt-4">
              Error: {accessError}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <HubStateProvider currentUser={currentUser}>
      <div className="bg-gradient-to-br from-slate-50 via-white to-teal-50">
        {/* Main Content */}
        <div>


          {/* Contextual Adaptive Header */}
          <div className="px-4">
            <AdaptiveHeader
              currentView={activeTab}
              portfolioStatus={portfolioStatus}
              studentProfile={studentProfile}
              onNavigate={setActiveTab}
              colleges={colleges}
            />
          </div>

        {/* College Selector - Compact when viewing content */}
        {activeTab === 'strategic' && colleges.length > 0 && (
          <div className="mb-6 px-4">
            <div className="flex items-center justify-between mb-3">
             
        
             
            </div>
            <CollegeSelector
              colleges={colleges}
              selectedColleges={selectedColleges}
              onSelectionChange={setSelectedColleges}
              includeAdditional={includeAdditional}
              onToggleAdditional={setIncludeAdditional}
            />
          </div>
        )}

        {/* Tab Content - Immediate, no extra scrolling */}
        <div className="min-h-[600px] px-4">
          {/* View 1: Comparative analysis */}
          {activeTab === 'strategic' && portfolioStatus.view1_unlocked && (
            <StrategicOverview
              colleges={comparisonData}
              currentUser={currentUser}
              portfolioLocked={portfolioStatus.tiers_locked}
              studentProfile={studentProfile}
              onDataRefresh={fetchUserColleges}
              currentView={activeTab}
              onViewChange={handleTabChange}
              portfolioStatus={portfolioStatus}
              onDeleteCollege={handleDeleteCollege}
            />
          )}

          {/* View 2: Tier Analysis */}
          {activeTab === 'tier' && portfolioStatus.view2_unlocked && (
            <TierAnalysis
              colleges={colleges}
              currentUser={currentUser}
              portfolioStatus={portfolioStatus}
              studentProfile={studentProfile}
              currentView={activeTab}
              onViewChange={handleTabChange}
              onDeleteCollege={handleDeleteCollege}
            />
          )}

          {/* View 3: Portfolio Strategy */}
          {activeTab === 'portfolio' && portfolioStatus.view3_unlocked && (
            <PortfolioStrategy
              colleges={colleges}
              currentUser={currentUser}
              portfolioStatus={portfolioStatus}
              studentProfile={studentProfile}
              currentView={activeTab}
              onViewChange={handleTabChange}
            />
          )}
        </div>

        {/* No colleges message */}
        {colleges.length === 0 && (
          <div className="bg-white rounded-xl border-2 border-slate-900 p-12 text-center mx-4">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No colleges in your portfolio yet
            </h3>
            <p className="text-gray-600 mb-6">
              Add colleges to your journey to start comparing them
            </p>
            <button
              onClick={() => navigate('/college')}
              className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
            >
              Go to College Journey
            </button>
          </div>
        )}

        {/* Portfolio In Progress Banner */}
        {activeTab === 'strategic' && !portfolioStatus.tiers_locked && colleges.length >= 12 && (
          <div className="bg-teal-50 border-2 border-teal-500 rounded-xl p-6 mt-8 mx-4">
            <div className="flex items-start gap-3">
              <Target className="w-6 h-6 text-teal-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-bold text-teal-900 mb-2">
                  Ready to Lock Your Portfolio?
                </h3>
                <p className="text-teal-800 text-xs leading-relaxed mb-3">
                  You have {colleges.length} colleges in your portfolio. Once you're confident with your 4-4-4 selection,
                  lock it to unlock <strong>Portfolio Strategy</strong> with strategic insights and application coordination tools.
                </p>
                <div className="bg-white border border-teal-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-teal-900 mb-2">What you'll get when you lock:</p>
                  <ul className="text-xs text-teal-800 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">•</span>
                      <span>ED/EA strategy recommendations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">•</span>
                      <span>Application timeline optimization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">•</span>
                      <span>Essay overlap analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">•</span>
                      <span>Portfolio confidence scoring</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </HubStateProvider>
  );
};

export default CollegeComparisonHub;